/**
 * @file `useFieldUI` — one field's local input state, its 600 ms debounced write
 * and its `formUndoStack` registration; consumed by every renderer under
 * `components/rendering/fields`.
 * Writes are dot-path `DocumentPersist.update`s with `{ debounce: true }`,
 * immediately drained by `flushPending`: arming the non-cancelling branch leaves
 * the `setMergeDebounced` another writer queued alone, and the flush is what
 * sends the write now AND gives back a promise, so a rejection still reaches the
 * toast, `markFailed` and — only then — `removePending`. Only `'user'`-origin
 * commits reach the undo stack, a `disabled` field owns no undo target at all,
 * and `scheduledValueRef` outlives its own write so consecutive undos read what
 * the previous one wrote.
 * `useFormDeriveLiveValue` layers over `externalValue` at read time — no effect —
 * so a field→field derive updates as the user types the source field instead of
 * one server round-trip later; every other derive shape resolves to `null` there
 * and the reconciler's persisted value stands. `useFormRecoveryFieldState` layers
 * over the same seam and outranks it: while a restore point is previewed the field
 * shows what that version held, and the page is `forceDisabled`, so nothing here
 * is writable while the substituted value is on screen.
 */
import { useContext, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import debounce from 'lodash/debounce';
import { Timestamp } from 'firebase/firestore';

import { AgentType, UIField, ValidationStatus } from '#types/System.types';
import type { EntityRecord, FormVersion } from '#types/System.types';

import { UserContext } from '../providers/UserContext';
import { FieldTypeContext } from '../providers/FieldTypeContext';
import { MessageContext } from '../providers/MessageContext';
import { pendingSavesActions } from '../store/pendingSavesStore';
import { CompositeFieldContext } from '../components/CompositeField/CompositeFieldContext';
import { formStore, formUndoStack, type UndoTarget } from '../store/form';
import { getEntityRecordPersist } from '../store/entityRecord/entityRecordStore';
import type { DocumentPersist } from '../store/Store.types';
import usePendingSaves from './usePendingSaves';
import { syncDocumentToSearch } from '../services/searchSyncService';
import { useFieldUIScope } from './useFieldUIScope';
import { useFormDeriveLiveValue } from './useFormDeriveLiveValue';
import { useFormRecoveryFieldState } from './form/useFormRecoveryFieldState';

/**
 * Options interface for the useFieldUI hook
 * @interface UseFieldUIOptions
 * @template T - The type of value managed by the field
 */
interface UseFieldUIOptions {
  /** The UI field configuration object containing field properties and metadata */
  uiField: UIField;
  /** Whether the field should be in read-only/disabled state */
  disabled?: boolean;
}

/**
 * Hook for managing field UI state, validation, and persistence in forms and entity records.
 * Provides real-time field value management with debounced saving to Firestore.
 *
 * @param {UseFieldUIOptions} options - Configuration options for the field UI
 * @returns {Object} Field state and management functions
 * @property {boolean} hasContext - Whether the field has valid form/entity context
 * @property {Object} fieldData - Current field data from Firestore
 * @property {T | null} value - Current field value
 * @property {T | null} persistedValue - Last successfully persisted value
 * @property {(newValue: T) => void} setValue - Function to update field value
 * @property {string | null} clipboardFieldKey - Which field this is,
 * `${documentPath}#${fieldId}`, and `null` only where identity is unavailable —
 * a composite sub-field or no document. The `formFieldClipboard` registration key,
 * which callers stamp on the DOM rather than recomputing it.
 * @property {boolean} disabled - The caller's own gate WIDENED by the composite
 * context's computed sub-fields. It refuses every write here and is what the
 * caller renders read-only, so a sub-field the platform writes — a formula target
 * or one a Form Derive projection fills — is disabled rather than editable-looking.
 * @property {boolean} writeRefused - `disabled` widened again by the layout
 * editor's preview surface. Whether the field ACCEPTS a write, as opposed to how
 * it renders, so a copy-only field registers a `null` clipboard writer.
 * @property {string | undefined} computedSubFieldReason - The sentence explaining
 * that widening, for the caller to hang on the control it disables. Set only for a
 * computed sub-field: every other reason to disable a field is already explained
 * where it is decided, by `FieldLabel`'s marks.
 * @property {ValidationStatus} validationStatus - Current validation status
 * @property {Object} props - Field type specific properties
 * @property {string} errorMessage - Error message for validation failures
 * @property {string} visibilityMode - Current visibility mode
 * @property {string} label - Field display label
 * @property {string} description - Field description
 * @property {FieldType} fieldType - Field type definition for this field
 */
export function useFieldUI<T>({ uiField, disabled: disabledProp = false }: UseFieldUIOptions) {
  const { user } = useContext(UserContext);
  const scope = useFieldUIScope(uiField.fieldId);
  const { fieldTypes, isLoading: fieldTypesLoading } = useContext(FieldTypeContext);
  const { messageApi, handleFirestoreWriteError } = useContext(MessageContext);
  const compositeFieldContext = useContext(CompositeFieldContext);

  /**
   * Why this sub-field is not the person's to edit — a formula on the table, or a
   * Form Derive projection — and `undefined` when nothing computes it. The lookup
   * is the membership test: a key is present exactly when a reason is.
   */
  const computedSubFieldReason = compositeFieldContext?.computedSubFieldReasons.get(uiField.fieldId);
  const disabled = disabledProp || computedSubFieldReason !== undefined;

  const [value, setValueInternal] = useState<T | null>(null);
  const [validationStatus, setValidationStatus] = useState<ValidationStatus>(undefined);
  const [hasAppliedDefault, setHasAppliedDefault] = useState(false);

  const dirtyRef = useRef<boolean>(false);
  const persistedValueRef = useRef<T | null>(null);
  const lastExternalValue = useRef<T | null>(null);
  const savingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const valueRef = useRef<T | null>(null);
  /**
   * The value this field last wrote or scheduled to write. Survives the write
   * itself — Firestore has not echoed yet — and is dropped only when the
   * external value is adopted, so a second undo reads what the first one wrote.
   */
  const scheduledValueRef = useRef<{ value: T | null } | null>(null);
  /**
   * Bumped by every edit and every undo. A save owns the dirty/pending
   * bookkeeping only while it is still the newest one; an out-of-band undo
   * write must not clear the flags of a keystroke that landed mid-flight.
   */
  const saveGenerationRef = useRef(0);

  const insideForm = scope.insideForm;
  const insideEntity = scope.insideEntity;
  const getField = scope.getField;

  const externalFieldValue = compositeFieldContext
    ? compositeFieldContext.compositeFieldValue?.fields?.[uiField.fieldId]?.value
    : scope.fieldValue;

  const visibilityMode = scope.visibilityMode;
  const fieldType = fieldTypes[uiField.fieldId];

  const label = fieldType?.description?.shortLabel || uiField.fieldId;
  const description = fieldType?.description?.longDescription ||
    fieldType?.description?.shortLabel;

  const setLocalValue = useCallback((next: T | null) => {
    valueRef.current = next;
    setValueInternal(next);
  }, []);

  const parseDefaultValue = useCallback((defaultValue: any, fieldDataTypeId?: string): any => {
    if (defaultValue === null || defaultValue === undefined || defaultValue === '') {
      return null;
    }

    if (fieldDataTypeId === 'number' || fieldDataTypeId === 'quantity' || fieldDataTypeId === 'currency' || fieldDataTypeId === 'percentage') {
      if (typeof defaultValue === 'string') {
        const parsed = parseFloat(defaultValue);
        return isNaN(parsed) ? null : parsed;
      }
      return typeof defaultValue === 'number' ? defaultValue : null;
    }

    if (fieldDataTypeId === 'yesNo') {
      if (typeof defaultValue === 'boolean') {
        return defaultValue;
      }
      if (typeof defaultValue === 'string') {
        const lower = defaultValue.toLowerCase().trim();
        return lower === 'true' || lower === 'yes' || lower === '1';
      }
      return defaultValue === 1 || defaultValue === '1';
    }

    return defaultValue;
  }, []);

  const effectiveDefaultValue = useMemo(() => {
    const rawDefaultValue = fieldType?.defaultValue;
    if (rawDefaultValue === null || rawDefaultValue === undefined || rawDefaultValue === '') {
      return null;
    }
    return parseDefaultValue(rawDefaultValue, fieldType?.fieldDataTypeId);
  }, [fieldType?.defaultValue, fieldType?.fieldDataTypeId, parseDefaultValue]);

  /**
   * A Form Derived Value this client can recompute as the user types. Only the
   * shapes reading nothing but this version's own fields qualify, and the hook
   * evaluates all of them together, so its answer matches the reconciler's for
   * every cycle confined to those shapes — but NOT for one closing through an
   * ArTable, which stays invisible to the client. Off inside an entity record, in
   * the layout-editor preview and inside a composite field: none of those three
   * reads `FormVersion.fields`, which is the only document a derive targets.
   * `null` means no live answer and the persisted value stands.
   */
  const liveDerivedValue = useFormDeriveLiveValue(
    uiField.fieldId,
    insideForm && !insideEntity && !scope.isPreview && !compositeFieldContext,
  );

  /**
   * What this field held at the restore point being previewed, when the preview's
   * read said it differs from live. Gated on the same scope `useFormDeriveLiveValue`
   * is: a point-in-time read covers this form version's own `FormVersion.fields`,
   * so an entity record, the layout-editor preview and a composite sub-field are
   * none of them addressed by it. The composite PARENT is a top-level field and is
   * addressed, and its retained object reaches its sub-fields through
   * `CompositeFieldProvider`'s `value` — which is why applying it again below that
   * provider would be a second, wrong application against the sub-field's own id.
   */
  const recovery = useFormRecoveryFieldState(uiField.fieldId);
  const showsRetainedValue =
    recovery.differs && insideForm && !insideEntity && !scope.isPreview && !compositeFieldContext;

  /**
   * The retained value outranks the live derive: a preview states what the version
   * held, and a derive recomputed from today's sources would not be it.
   */
  const externalValue = showsRetainedValue
    ? ((recovery.retained?.value ?? null) as T | null)
    : liveDerivedValue
      ? ((liveDerivedValue.value ?? null) as T | null)
      : (externalFieldValue as T | undefined) ?? null;
  const shouldApplyDefault = externalValue === null && effectiveDefaultValue !== null;

  const entityId = scope.entityId || undefined;
  const recordId = scope.recordId;
  const formId = scope.formId;
  const versionId = scope.versionId;

  const pendingId = usePendingSaves({
    insideForm,
    insideEntity,
    formId,
    versionId,
    entityId,
    recordId,
    identifier: `field:${uiField.fieldId}`,
  });

  /**
   * The document this field writes to, on the same entity-before-form precedence
   * `resolvePersist` uses below. Derived here rather than through
   * `resolveContextRoutes`, which would build a `DocumentReference` nothing reads.
   */
  const path = useMemo(
    () => insideEntity && entityId && recordId
      ? `entities/${entityId}/records/${recordId}`
      : insideForm && formId && versionId
        ? `forms/${formId}/formVersions/${versionId}`
        : 'unknown',
    [insideForm, insideEntity, formId, versionId, entityId, recordId],
  );

  /** The version id or record id — whichever the resolved path ends in. */
  const docId = path.split('/').at(-1) ?? '';

  /**
   * Whether this field accepts a write at all: the caller's gate widened by the
   * layout editor's preview surface, whose mock document nothing may write to.
   * Separate from `disabled`, which is what the control renders read-only from.
   */
  const writeRefused = disabled || scope.isPreview;

  /**
   * Which field this is, for `formFieldClipboard`. `null` only where identity is
   * genuinely unavailable: a composite sub-field writes through its parent and
   * owns no path, and `'unknown'` is no document. A field that refuses writes
   * still names itself, so Copy Field reaches a derived value, a protected field
   * and whatever a recovery preview is showing.
   */
  const clipboardFieldKey = compositeFieldContext || path === 'unknown'
    ? null
    : `${path}#${uiField.fieldId}`;

  /**
   * The same path narrowed to what ⌘Z may write. Undo is a write verb, so a field
   * refusing writes owns no target rather than one that would refuse: a locked
   * record must not be reachable by ⌘Z at all.
   */
  const undoTargetKey = writeRefused ? null : clipboardFieldKey;

  const resolvePersist = useCallback(
    (): DocumentPersist<EntityRecord> | DocumentPersist<FormVersion> | null =>
      insideEntity && entityId && recordId
        ? getEntityRecordPersist(entityId, recordId)
        : insideForm && formId && versionId
          ? formStore.getFormVersionPersist()
          : null,
    [insideEntity, entityId, recordId, insideForm, formId, versionId],
  );

  /**
   * The raw write, and the only place that touches Firestore. `update` arms the
   * non-cancelling debounced branch — it must not discard the payload another
   * writer queued on the same document — and `flushPending` then sends it now
   * and reports whether it landed. Rejects on failure; reporting is the caller's.
   */
  const writeFieldValue = useCallback(async (newValue: T | null) => {
    const persist = resolvePersist();
    if (!persist) {
      throw new Error('Missing required context for saving field');
    }

    const now = Timestamp.now();
    const fieldMeta = `fields.${uiField.fieldId}.meta`;

    await persist.update(
      {
        [`fields.${uiField.fieldId}.value`]: newValue,
        [`${fieldMeta}.lastModified`]: now,
        [`${fieldMeta}.userId`]: user?.uid,
        [`${fieldMeta}.agentType`]: AgentType.User,
        'meta.lastModified': now,
        'meta.userId': user?.uid,
        'meta.agentType': AgentType.User,
      },
      { debounce: true },
    );
    await persist.flushPending();
    persistedValueRef.current = newValue;

    if (uiField.triggerSearchSync) {
      if (insideForm && formId && versionId) {
        try {
          void syncDocumentToSearch('formVersions', formId, versionId);
        } catch (err) {
          console.warn('[useFieldUI] Failed to sync formVersion to search:', err);
        }
      }

      if (insideEntity && entityId && recordId) {
        try {
          void syncDocumentToSearch('records', entityId, recordId);
        } catch (err) {
          console.warn('[useFieldUI] Failed to sync record to search:', err);
        }
      }
    }
  }, [resolvePersist, insideEntity, insideForm, entityId, recordId, formId, versionId, uiField.fieldId, uiField.triggerSearchSync, user?.uid]);

  /**
   * The autosave path: reports its own failure and never rejects, because its
   * caller is a debounce timer. `generation` guards the bookkeeping — a save owns
   * the dirty/pending flags only while it is still the newest one.
   */
  const saveFieldValue = useCallback(async (newValue: T | null, generation: number) => {
    try {
      await writeFieldValue(newValue);
    } catch (error) {
      const userMessage = `Error saving field ${label}. Changes were not saved.`;
      handleFirestoreWriteError(error, '[useFieldUI] debouncedSave', { userMessage, saveId: pendingId });
      setValidationStatus('error');
    } finally {
      if (saveGenerationRef.current === generation) {
        pendingSavesActions.removePending(pendingId);
        dirtyRef.current = false;
        if (savingTimeoutRef.current) {
          clearTimeout(savingTimeoutRef.current);
          savingTimeoutRef.current = null;
        }
      }
    }
  }, [writeFieldValue, handleFirestoreWriteError, label, pendingId]);

  const debouncedSave = useMemo(() => debounce(saveFieldValue, 600), [saveFieldValue]);

  /**
   * `⌘S` has two debounces to clear: this hook's 600 ms one, and the
   * `SAVE_DEBOUNCE_MS` one `persist.update({ debounce: true })` arms behind it —
   * the second one drained again here for writes other components armed on the
   * same document. The rejection is swallowed rather than reported: `flushAll`
   * runs every field's flusher in one loop, and `saveFieldValue` has already
   * toasted and marked failed whatever this field itself had queued.
   */
  useEffect(() => pendingSavesActions.registerFlusher(() => {
    void (async () => {
      await debouncedSave.flush();
      await resolvePersist()?.flushPending();
    })().catch(() => {});
  }), [debouncedSave, resolvePersist]);

  /**
   * `apply` rethrows so `formUndoStack.step` never files a revert that did not
   * land onto the redo stack; `useFormUndoKeybindings` owns the single
   * user-facing report, which is why this marks the save failed but does not
   * toast. A disabled field is unregistered rather than guarded, so ⌘Z on a
   * locked record resolves `unavailable` instead of writing.
   */
  const undoTarget = useMemo<UndoTarget>(() => ({
    read: () => scheduledValueRef.current
      ? scheduledValueRef.current.value
      : getField()?.value ?? null,
    apply: async (_docId, writes) => {
      const reverted = (writes.at(-1)?.value ?? null) as T | null;
      debouncedSave.cancel();
      dirtyRef.current = false;
      scheduledValueRef.current = { value: reverted };
      setLocalValue(reverted);
      pendingSavesActions.removePending(pendingId);
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
        savingTimeoutRef.current = null;
      }
      saveGenerationRef.current += 1;

      try {
        await writeFieldValue(reverted);
      } catch (error) {
        pendingSavesActions.markFailed(pendingId);
        setValidationStatus('error');
        throw error;
      }
    },
  }), [getField, debouncedSave, writeFieldValue, setLocalValue, pendingId]);

  useEffect(() => {
    if (!undoTargetKey) {
      return;
    }
    return formUndoStack.registerTarget(undoTargetKey, undoTarget);
  }, [undoTargetKey, undoTarget]);

  /**
   * The single write path. `origin` decides undo capture and nothing else: only
   * `'user'` edits belong on the stack, so the auto-applied field-type default
   * persists exactly like a keystroke but leaves the first ⌘Z to the user's own
   * first edit.
   */
  const commitValue = useCallback((newValue: T, origin: 'user' | 'default') => {
    if (disabled) {
      return;
    }

    if (compositeFieldContext) {
      setLocalValue(newValue);
      const isValid = fieldType?.validation ? fieldType.validation(newValue) : true;
      setValidationStatus(isValid ? undefined : 'error');
      if (isValid) {
        compositeFieldContext.setSubFieldValue(uiField.fieldId, newValue);
      }
      return;
    }

    const previousValue = valueRef.current;
    setLocalValue(newValue);
    dirtyRef.current = true;
    pendingSavesActions.addPending(pendingId);

    if (savingTimeoutRef.current) {
      clearTimeout(savingTimeoutRef.current);
    }

    savingTimeoutRef.current = setTimeout(() => {
      if (dirtyRef.current) {
        console.warn('Saving to database is taking longer than expected');
        messageApi.warning('Saving to database is taking longer than expected');
      }
    }, 10000);

    const isValid = fieldType?.validation ? fieldType.validation(newValue) : true;
    setValidationStatus(isValid ? undefined : 'error');
    if (!isValid) {
        debouncedSave.cancel();
        pendingSavesActions.removePending(pendingId);
        dirtyRef.current = false;
        if (savingTimeoutRef.current) {
          clearTimeout(savingTimeoutRef.current);
          savingTimeoutRef.current = null;
        }
        return;
    }

    if (undoTargetKey && origin === 'user') {
      formUndoStack.record(undoTargetKey, [{
        docId,
        path: `fields.${uiField.fieldId}.value`,
        prev: previousValue ?? null,
        next: newValue ?? null,
      }]);
    }

    scheduledValueRef.current = { value: newValue };
    saveGenerationRef.current += 1;
    debouncedSave(newValue, saveGenerationRef.current);
  }, [debouncedSave, fieldType, messageApi, disabled, compositeFieldContext, uiField.fieldId, pendingId, setLocalValue, undoTargetKey, docId]);

  const setValue = useCallback((newValue: T) => commitValue(newValue, 'user'), [commitValue]);

  /**
   * Locking a record disables every field in it, which must not cost the edit
   * still sitting in the 600 ms window: a dirty field FLUSHES rather than
   * cancels, so the same `saveFieldValue` runs now and its own `finally` clears
   * the dirty/pending/timeout bookkeeping. Dirty means an edit is genuinely
   * unsettled — a rejected value never leaves the flag set, because
   * `commitValue`'s invalid branch clears it. Only a clean field takes the
   * discard path — there is nothing to lose.
   */
  useEffect(() => {
    if (!disabled) {
      return;
    }

    if (dirtyRef.current) {
      void debouncedSave.flush();
      return;
    }

    debouncedSave.cancel();
    dirtyRef.current = false;
    scheduledValueRef.current = null;
    pendingSavesActions.removePending(pendingId);
    if (savingTimeoutRef.current) {
      clearTimeout(savingTimeoutRef.current);
      savingTimeoutRef.current = null;
    }
  }, [disabled, debouncedSave, pendingId]);

  useEffect(() => {
    if (externalValue !== lastExternalValue.current) {
      lastExternalValue.current = externalValue;
      if (!dirtyRef.current) {
        /**
         * The listener has spoken: whatever this field last wrote is either
         * confirmed or superseded, so the undo target reads the live value again.
         */
        scheduledValueRef.current = null;
        setLocalValue(externalValue);
        if (externalValue !== null) {
          setHasAppliedDefault(false);
        }
      }
      pendingSavesActions.removePending(pendingId);
      dirtyRef.current = false;
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
        savingTimeoutRef.current = null;
      }
    }
  }, [externalValue, pendingId, setLocalValue]);

  useEffect(() => {
    if (disabled) {
      return;
    }
    if (shouldApplyDefault && effectiveDefaultValue !== null && !dirtyRef.current && !hasAppliedDefault) {
      setHasAppliedDefault(true);
      commitValue(effectiveDefaultValue, 'default');
    }
  }, [shouldApplyDefault, effectiveDefaultValue, hasAppliedDefault, commitValue, disabled]);

  useEffect(() => {
    return () => {
      debouncedSave.cancel();
      dirtyRef.current = false;
      scheduledValueRef.current = null;
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
        savingTimeoutRef.current = null;
      }
    };
  }, [debouncedSave]);


  if (!insideForm && !insideEntity && !compositeFieldContext) {
    return {
      hasContext: false,
      value: null,
      setValue: () => {},
      disabled,
      writeRefused,
      computedSubFieldReason,
      clipboardFieldKey,
    };
  }

  return {
    hasContext: insideEntity || insideForm || !!compositeFieldContext,
    value,
    disabled,
    writeRefused,
    clipboardFieldKey,
    computedSubFieldReason,
    persistedValue: persistedValueRef.current,
    setValue,
    validationStatus,
    props: fieldType?.props ?? {},
    errorMessage: fieldType?.errorMessage ?? '',
    visibilityMode,
    label,
    description,
    fieldType,
    fieldTypesLoading,
  };
}
